import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'

import { listApiTokens, listApplications } from '../../api/portal'
import { DataState } from '../../components/DataState'
import { EmptyState } from '../../components/ui/EmptyState'
import { CodeBlock } from '../../components/ui/CodeBlock'
import { Fact } from '../../components/ui/Fact'
import { Ltr } from '../../components/ui/Ltr'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { PageHeader } from '../../components/ui/PageHeader'
import { currentApiEnvironment } from '../services/environments'
import { ApiTokensSection } from '../applications/ApiTokensSection'
import { MachineNetworksPanel } from '../applications/MachineNetworksPanel'
import { can } from '../../auth/session'
import { formatCount } from '../../format'
import { usePortal } from '../../auth/sessionContext'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { presentation } from '../../portalMode'


/**
 * Everything needed to point one piece of software at this platform.
 *
 * This replaced a seven-column table of applications. The table was accurate
 * and answered a question nobody had: a customer arriving at «اتصال API» wants
 * to know whether they can call the API yet, and what to paste where — not how
 * many requests each application made in thirty days.
 *
 * So the page states the four facts an integration needs, in the order it
 * needs them, and puts the token machinery underneath. The application picker
 * appears only when there is more than one to pick.
 */
export function ConnectionPage() {
  const LIVE = currentApiEnvironment()
  const { membership } = usePortal()
  const view = presentation(membership)
  const customerId = membership.customer.id
  const [selected, setSelected] = useState<string | null>(null)

  const applications = useQuery({
    queryKey: ['client', 'applications', customerId],
    queryFn: ({ signal }) => listApplications(customerId, signal),
  })
  const rows = applications.data?.results ?? []
  // Derived, not synchronised. Holding the chosen id in state *and* copying
  // the first row into it from an effect is two sources for one fact, and the
  // copy runs on every render until they agree.
  const application = rows.find((row) => row.id === selected) ?? rows[0]

  const tokens = useQuery({
    queryKey: ['client', 'api-tokens', application?.id],
    queryFn: ({ signal }) => listApiTokens(customerId, application!.id, signal),
    enabled: application !== undefined,
  })
  const issued = Array.isArray(tokens.data) ? tokens.data : []
  const live = issued.filter((token) => token.status === 'active')
  const username = issued[0]?.username ?? null

  /** Base URL, headers and a working call. Shown plainly to a development
   *  shop; folded behind a disclosure for a business. */
  const technical = (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Fact label="نشانی پایه" hint={LIVE.labelFa}>
            <Ltr mono>{LIVE.baseUrl}</Ltr>
          </Fact>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Fact label="سرآیندهای لازم">
            <Stack spacing={0.25}>
              <Ltr mono>Authorization</Ltr>
              <Ltr mono>X-API-Username</Ltr>
            </Stack>
          </Fact>
        </Grid>
      </Grid>
      <CodeBlock
        title="یک فراخوانی نمونه"
        caption="جای <TOKEN> توکن خودتان و جای <USERNAME> نام کاربری بالا را بگذارید."
        code={[
          `curl -X POST ${LIVE.baseUrl}/api/v1/banking/card-to-iban/ \\`,
          `  -H "Authorization: Bearer <TOKEN>" \\`,
          `  -H "X-API-Username: ${username ?? '<USERNAME>'}" \\`,
          '  -H "Idempotency-Key: 9f2b41c8-1d3e-4a77-8b52-0c6d1e4a7f10" \\',
          '  -H "Content-Type: application/json" \\',
          `  -d '{"card_number": "6037991234567890"}'`,
        ].join('\n')}
      />
      {window.location.hostname !== 'capi.mobinsandogh.ir' ? <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          محیط عملیاتی هنوز راه‌اندازی نشده است؛ فعلاً همهٔ فراخوانی‌ها روی نشانی بالا انجام
          می‌شود.
        </Typography>
      </Box> : null}
    </>
  )

  return (
    <>
      <PageHeader
        title={view.isService ? 'اتصال نرم‌افزار' : 'اتصال API'}
        description={
          view.isService
            ? 'دو مقدار زیر را در بخش تنظیمات نرم‌افزار خود وارد کنید تا به سامانه وصل شود.'
            : 'آنچه برای وصل‌کردن نرم‌افزار خودتان به این پلتفرم لازم دارید: نشانی پایه، نام کاربری و توکن.'
        }
      />

      <DataState
        isPending={applications.isPending}
        isError={applications.isError}
        error={applications.error}
        onRetry={() => void applications.refetch()}
      >
        {application === undefined ? (
          <EmptyState
            title="هنوز اپلیکیشنی برای این حساب ساخته نشده است"
            hint="اپلیکیشن‌ها را ما می‌سازیم. برای شروع یکپارچه‌سازی با پشتیبانی تماس بگیرید."
          />
        ) : (
          <Stack spacing={3}>
            <SectionCard
              title="وضعیت اتصال"
              action={
                rows.length > 1 ? (
                  <TextField
                    select
                    size="small"
                    label="اپلیکیشن"
                    value={application.id}
                    onChange={(event) => setSelected(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {rows.map((row) => (
                      <MenuItem key={row.id} value={row.id}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null
              }
            >
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Fact label="اپلیکیشن">{application.name}</Fact>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Fact label="وضعیت">
                    <StatusChip status={application.status} />
                  </Fact>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Fact
                    label="نام کاربری API"
                    hint={username ? undefined : 'پس از دریافت نخستین توکن مشخص می‌شود.'}
                  >
                    {username ? <Ltr mono>{username}</Ltr> : '—'}
                  </Fact>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Fact
                    label="توکن API"
                    hint={
                      live.length > 0
                        ? `${formatCount(live.length)} توکن فعال`
                        : 'برای اتصال نرم‌افزار خود یک توکن دریافت کنید.'
                    }
                  >
                    <StatusChip status={live.length > 0 ? 'active' : 'inactive'} />
                  </Fact>
                </Grid>
              </Grid>
            </SectionCard>

            {view.foldsTechnicalDetail ? (
              // Not deleted — folded. The product owner asked for the
              // developer material to stay available for customers who grow
              // into it, and a business customer should not meet a curl
              // command on the way to copying a token.
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    جزئیات فنی اتصال
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>{technical}</AccordionDetails>
              </Accordion>
            ) : (
              <SectionCard
                title="چطور فراخوانی کنم"
                description="سه چیز در هر فراخوانی لازم است: نشانی پایه، نام کاربری و توکن. توکن در سرآیند Authorization می‌رود."
              >
                {technical}
              </SectionCard>
            )}

            {tokens.isError ? (
              <Alert severity="warning">
                وضعیت توکن‌ها خوانده نشد. بخش زیر را دوباره باز کنید.
              </Alert>
            ) : null}

            <ApiTokensSection
              customerId={customerId}
              applicationId={application.id}
              simple={view.isService}
            />
            <MachineNetworksPanel key={application.id} endpoint={`/customers/${customerId}/applications/${application.id}/networks/`}
              canEdit={can(membership, 'update_application')} />
          </Stack>
        )}
      </DataState>
    </>
  )
}
